import { DeleteOutlined, FileAddOutlined } from '@ant-design/icons';
import {
  Button,
  Card,
  Divider,
  Modal,
  Tabs,
  Typography
} from 'antd';
import _ from 'lodash';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';
import allActions from '../../../actions';
import PageWrapper from '../../../components/pageWrapper';
import { listModes } from '../../../utils/GlobalConstants';
import CreateStaticWLModal from './createStaticWLModal/CreateStaticWLModal';
import StaticWorklistTable from './staticWorklistTable/StaticWorklistTable';
import styles from './styles.module.scss';

const { Title } = Typography;

const StaticWorklistsContainer = () => {
  const materialWorklists = useSelector((state) => state.worklistState.staticWorklists);
  const mmWorklists = useSelector((state) => state.worklistState.mmStaticWorklists);
  const userState = useSelector((state) => state.authState.user);
  const defaultWLTab = useSelector(
    (state) => state.commonState.userSettings?.STATIC_WORKLIST_DEFAULT_TAB,
  );
  const dispatch = useDispatch();
  const [selectedMatItems, setSelectedMatItems] = useState([]);
  const [selectedMMItems, setSelectedMMItems] = useState([]);
  const [deleteModal, setDeleteModal] = useState(false);
  const [createModal, setCreateModal] = useState(false);
  const { matList, mmList } = listModes;
  const [currSelectedItems, setCurrSelectedItems] = useState([]);
  const [mmRowKeys, setMMRowKeys] = useState([]);
  const [matRowKeys, setMatRowKeys] = useState([]);
  const [mode, setMode] = useState(matList);
  const [wlTab, setWlTab] = useState(listModes.matList);
  const { TabPane } = Tabs;

  useEffect(() => {
    dispatch(allActions.WorkListActions.clearStaticWorklist(listModes.matList));
    dispatch(allActions.WorkListActions.clearStaticWorklist(listModes.mmList));
  }, []);

  useEffect(() => {
    if (userState?.uniqueId) {
      dispatch(allActions.WorkListActions.getStaticWLs(userState.uniqueId, matList));
      dispatch(allActions.WorkListActions.getStaticWLs(userState.uniqueId, mmList));
    }
  }, [userState?.uniqueId]);

  const setDelete = () => {
    setDeleteModal(true);
  };

  const setCreate = () => {
    setCreateModal(true);
  };

  const deleteWorklists = () => {
    // change list of worklist objects to array of array of worklistIDs
    const worklistIDList = currSelectedItems.map((e) => [e.WORKLIST_ID]);
    dispatch(allActions.WorkListActions.deleteStaticWLs(worklistIDList, userState.uniqueId, mode));
    setDeleteModal(false);
  };

  const onTabChange = (key) => {
    setSelectedMatItems([]);
    setMMRowKeys([]);
    setMatRowKeys([]);
    setMode(key);
    setWlTab(key);
  };

  useEffect(() => {
    setCurrSelectedItems(_.cloneDeep(selectedMMItems));
  }, [selectedMMItems]);

  useEffect(() => {
    setCurrSelectedItems(_.cloneDeep(selectedMatItems));
  }, [selectedMatItems]);

  useEffect(() => {
    if (mmRowKeys?.length) {
      const tempRows = [];
      selectedMMItems.forEach((x) => {
        if (mmWorklists?.find(
          (wl) => x?.WORKLIST_ID && wl?.WORKLIST_ID === x.WORKLIST_ID,
        )) tempRows.push(x);
      });
      setSelectedMMItems(tempRows);
    }
    if (matRowKeys?.length) {
      const tempRows = [];
      selectedMatItems.forEach((x) => {
        if (materialWorklists?.find(
          (wl) => x?.WORKLIST_ID && wl?.WORKLIST_ID === x.WORKLIST_ID,
        )) tempRows.push(x);
      });
      setSelectedMatItems(tempRows);
    }
  }, [mmWorklists, materialWorklists]);

  const createWorklist = (worklistName, worklistDescription) => {
    const regexStr = /^\s+|\s+$/g; // Remove line breaks and spaces at both ends of the string.
    const tempWLName = worklistName?.replace(regexStr, '');
    const tempWLDescr = worklistDescription?.replace(regexStr, '');
    if (!userState?.uniqueId) return;
    const metadata = {
      userID: userState.uniqueId,
      worklistID: uuidv4(),
      noOfRecords: 0,
      worklistName: tempWLName,
      userEmail: userState?.account?.username || null,
      worklistDescription: tempWLDescr,
    };
    dispatch(allActions.WorkListActions.createStaticWL(metadata, {}, [], mode));
    setCreateModal(false);
  };

  useEffect(() => {
    if (defaultWLTab) onTabChange(defaultWLTab);
  }, [defaultWLTab]);

  const tableOperations = (
    (
      <div>
        <div className={styles.divFloatRight}>
          <Button type="primary" onClick={() => setCreate()}>
            <FileAddOutlined className={styles.icon} />
            Create new Static Worklist
          </Button>
          <Divider type="vertical" />
          <Button type="primary" disabled={!currSelectedItems.length} onClick={() => setDelete()}>
            <DeleteOutlined />
            Delete selected items
          </Button>
        </div>
      </div>
    )
  );

  return (
    <PageWrapper topPadding>
      <div className={styles.pageHeader}>
        <Title level={4} style={{ color: 'var(--neutral--100) !important' }}>Static Worklists</Title>
        {tableOperations}
      </div>

      <Tabs activeKey={wlTab} onChange={(key) => onTabChange(key)}>
        <TabPane tab="Material List" key={matList}>
          <StaticWorklistTable
            worklists={materialWorklists}
            worklistCount={materialWorklists?.length ?? 0}
            updateSelectedItems={setSelectedMatItems}
            wlRoute="static-work-list"
            selectedRowKeys={matRowKeys}
            setSelectedRowKeys={setMatRowKeys}
          />
        </TabPane>
        <TabPane tab="Material Master List" key={mmList}>
          <StaticWorklistTable
            worklists={mmWorklists}
            worklistCount={mmWorklists?.length ?? 0}
            updateSelectedItems={setSelectedMMItems}
            wlRoute="material-master/static-work-list"
            selectedRowKeys={mmRowKeys}
            setSelectedRowKeys={setMMRowKeys}
          />
        </TabPane>
      </Tabs>
      <CreateStaticWLModal
        createModal={createModal}
        setCreateModal={setCreateModal}
        onCreateWorklist={createWorklist}
      />
      <Modal
        title="Delete"
        visible={deleteModal}
        onOk={() => deleteWorklists()}
        onCancel={() => setDeleteModal(false)}
        okText="Confirm"
      >
        Are you sure you want to delete the following worklists?
        <Card>
          {currSelectedItems.map((worklist) => (
            <div key={worklist.WORKLIST_ID}>
              {worklist.WorklistName}
              <br />
            </div>
          ))}
        </Card>
      </Modal>
    </PageWrapper>
  );
};

export default StaticWorklistsContainer;
